import { useCallback, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useChannelSettingsContext } from '@sendbird/uikit-react/ChannelSettings/context';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslate } from 'ra-core';
import {
  GROUP_CHANNEL_MAX_NUMBER_OF_MEMBERS,
  ONE_TO_ONE_MAX_NUMBER_OF_MEMBERS,
  SUPERGROUP_CHANNEL_MAX_NUMBER_OF_MEMBERS,
} from '../GroupCustomChannelList/CreateGroupsModal';
import { ChannelUserInviteCard } from '../ChannelUserInviteCard';
import { SearchTextInput } from '../SearchTextInput';
import { UserInfiniteList } from '../UserInfiniteList';
import { useGetUsers, type UserDto } from '../../../hooks/useGetUsers';
import { useInviteToChannel } from '../../../api/hooks/sendbird/useInviteToChannel';
import { useHasPermission } from '../../../auth/useHasPermission';
import { Button } from '../../../components/ui/button';
import { ChannelSettingsModal } from './ChannelSettingsModal';

type UserId = string;
export interface InviteUsersModalProps {
  open: boolean;
  onCancel(): void;
  onSubmit(userIds: UserId[]): void;
}

export function InviteUsersModal({
  onCancel,
  onSubmit,
  open,
}: InviteUsersModalProps) {
  const translate = useTranslate();
  const { channel } = useChannelSettingsContext();
  const [selectedUsers, setSelectedUsers] = useState<UserDto[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const canCreateMultiUserChannel = useHasPermission(
    'CHAT_CHANNEL_CREATE_MULTI_USERS'
  );

  const {
    data: users,
    isLoading,
    isFetchingNextPage,
    refetch,
    fetchNextPage,
    hasNextPage,
    error,
    isError,
    isFetchNextPageError,
    isRefetching,
  } = useInfiniteQuery(
    useGetUsers({ query: searchTerm, notMemberOfChannel: channel.url })
  );

  const { mutateAsync: inviteUsersAsync, isPending: isInvitingUsers } =
    useInviteToChannel();

  const currentMembersLength = channel.memberCount;

  const maxUsers = useMemo(() => {
    const maxMembers = canCreateMultiUserChannel
      ? channel.isSuper
        ? SUPERGROUP_CHANNEL_MAX_NUMBER_OF_MEMBERS
        : GROUP_CHANNEL_MAX_NUMBER_OF_MEMBERS
      : ONE_TO_ONE_MAX_NUMBER_OF_MEMBERS;

    return Math.max(0, maxMembers - currentMembersLength);
  }, [canCreateMultiUserChannel, channel.isSuper, currentMembersLength]);

  const flattenedUsers = useMemo(() => {
    if (!users?.pages) return [];

    return users.pages
      .flatMap((page) => page.data)
      .map((user) => ({ ...user, userId: user.id.toString() }));
  }, [users]);

  const handleInviteUsers = useCallback(async () => {
    const userIdsToInvite = selectedUsers.map((user) => `${user.id}`);

    await inviteUsersAsync({
      channelUrl: channel.url,
      userIds: userIdsToInvite,
    });
    onSubmit(userIdsToInvite);
    setSelectedUsers([]);
  }, [selectedUsers, inviteUsersAsync, onSubmit, channel.url]);

  return (
    <ChannelSettingsModal
      open={open}
      onCancel={onCancel}
      title={translate('chat.inviteUsersModalTitle')}
      header={
        <SearchTextInput
          label={translate('chat.searchMembers')}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
        />
      }
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onCancel}>
            {translate('ra.action.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => void handleInviteUsers()}
            disabled={isInvitingUsers || selectedUsers.length === 0}
          >
            {isInvitingUsers && <Loader2 className="animate-spin" />}
            {translate('chat.invite', {
              selected: selectedUsers.length + currentMembersLength,
              max: canCreateMultiUserChannel
                ? channel.isSuper
                  ? SUPERGROUP_CHANNEL_MAX_NUMBER_OF_MEMBERS
                  : GROUP_CHANNEL_MAX_NUMBER_OF_MEMBERS
                : ONE_TO_ONE_MAX_NUMBER_OF_MEMBERS,
            })}
          </Button>
        </>
      }
    >
      <UserInfiniteList
        users={flattenedUsers}
        isLoadingUsers={isFetchingNextPage}
        isPendingUsers={isLoading}
        refetchUsers={refetch}
        fetchNextPageUsers={fetchNextPage}
        errorUsers={error}
        isErrorUsers={isError}
        isFetchNextPageError={isFetchNextPageError}
        isRefetching={isRefetching}
        hasNextPageUsers={hasNextPage}
        renderUser={(user) => (
          <ChannelUserInviteCard
            user={user}
            selectedUsers={selectedUsers}
            setSelectedUsers={setSelectedUsers}
            maxUsers={maxUsers}
          />
        )}
      />
    </ChannelSettingsModal>
  );
}
